import React, { ReactNode } from 'react';
import { Field, Form } from 'react-final-form';
import { FORM_ERROR, FormApi } from 'final-form';
import { I18n } from 'react-redux-i18n';
import TextInput from '../../../../common/inputs/TextInput';
import Button from '../../../../common/Button';
import { CompteProfilProProfileDetails } from '../../../../../network/api/types/compte/CompteProfilProProfileDetails';
import { CompteProfilProModifyDTO } from '../../../../../network/api/types/compte/CompteProfilProModifyDTO';
import compteApi from '../../../../../network/api/compteApi';
import { AutocompleteSource } from '../../../../common/inputs/autocompleteInput/AutocompleteSource';
import { MonIdentite } from '../../../../../network/api/types/compte/MonIdentite';
import { FieldErrorDTO } from '../../../../../network/api/types/compte/FieldErrorDTO';
import { ErrorDTO } from '../../../../../network/api/types/compte/ErrorDTO';
import { notifyErrorWithMessage, notifySuccess } from '../../../../common/Notification';
import { validateEmail, validatePhone } from '../../../../../services/utils';
import ConfirmationModal from '../../../../common/modals/ConfirmationModal';
import ChipsListAutocomplete from '../../../../common/chips/ChipsListAutocomplete';
import { TypeEntityEnum } from '../../../../../network/api/types/compte/Entity';

type Props = {
  onClose: () => void
  idnRattachement: string
  setDirty: (dirty: boolean, callback?: () => void) => void
  setModalI18n: (title: string, subtitle: string) => void
};

type State = {
  profileDetails: CompteProfilProProfileDetails
  myIdentity: MonIdentite
  langs: AutocompleteSource[]
  selectedLangs: AutocompleteSource[]
  langsDirty: boolean
  langError: string
  domaines: AutocompleteSource[]
  selectedDomaines: AutocompleteSource[]
  domainesDirty: boolean
  domaineError: string
  submitted: boolean
  langModalOpen: boolean
  domaineModalOpen: boolean
  initialValues: FormValues
  confirmModalOpen: boolean
  isLoading: boolean
};

type FormValues = {
  idnRattachement?: string
  fonction?: string
  email?: string
  telephoneFixe?: string
  telephoneMobile?: string
  langsSelect?: string
  langs?: string[]
  domainesSelect?: string
  domaines?: string[]
  statut?: string
};

class ProfessionalInformationForm extends React.Component<Props, State> {
  state: State = {
    profileDetails: null,
    myIdentity: null,
    langs: [],
    selectedLangs: [],
    langsDirty: false,
    langError: '',
    domaines: [],
    selectedDomaines: [],
    domainesDirty: false,
    domaineError: '',
    submitted: false,
    langModalOpen: false,
    domaineModalOpen: false,
    initialValues: {},
    confirmModalOpen: false,
    isLoading: false,
  };

  componentDidMount(): void {
    compteApi.getProfilePro(this.props.idnRattachement)
      .then((profileDetails: CompteProfilProProfileDetails) => this.setState({ profileDetails },
        () => this.setState((prevState) => ({
          initialValues: {
            idnRattachement: prevState.profileDetails?.idnRattachement,
            fonction: prevState.profileDetails?.fonction,
            email: prevState.profileDetails?.email,
            telephoneFixe: prevState.profileDetails?.telephoneFixe,
            telephoneMobile: prevState.profileDetails?.telephoneMobile,
            statut: prevState.profileDetails?.statut,
          },
        }))))
      .then(() => compteApi.getDomainesDactivite()
        .then((domaines) => {
          this.setState({
            domaines: domaines.map((domaine) => ({
              id: domaine,
              label: domaine,
            })),
          });
        }));
    compteApi.fetchAllLangs()
      .then((langs) => {
        this.setState({
          langs: langs.langs.map((lang) => ({
            id: lang.code,
            label: lang.name,
          })),
        });
      })
      .then(() => compteApi.fetchMyIdentity()
        .then((myIdentity) => {
          this.setState({ myIdentity });
        }))
      .then(() => this.setState({ langsDirty: false }, () => this.props.setDirty(false)));
    this.props.setModalI18n('HOME.CONFIRM_MODAL_TITLE', 'PROFESSIONAL_INFORMATION.CONFIRM_CLOSE');
  }

  openModal = (field: 'lang' | 'domaine' | 'save'): void => {
    if (field === 'lang') {
      this.setState((prevState) => ({ langModalOpen: !prevState.langModalOpen }));
    } else if (field === 'domaine') {
      this.setState((prevState) => ({ domaineModalOpen: !prevState.domaineModalOpen }));
    } else {
      this.setState((prevState) => ({ confirmModalOpen: !prevState.confirmModalOpen }));
    }
  };

  isFormDirty = (form: FormApi<FormValues, Partial<FormValues>>): boolean =>
    this.state.langsDirty
    || this.state.domainesDirty
    || form.getRegisteredFields().filter((fieldName) => !['langsSelect', 'domainesSelect', 'langs', 'domaines'].includes(fieldName))
      .some((fieldName) => {
        const fieldState = form.getFieldState(fieldName as keyof FormValues);
        return this.state.submitted ? fieldState.dirtySinceLastSubmit : fieldState.dirty;
      })
    || (this.langsContain(form.getFieldState('langsSelect').value))
    || (this.domainesContain(form.getFieldState('domainesSelect').value));

  onFormChange = (form: FormApi<FormValues, Partial<FormValues>>): void => {
    const setDirty = this.isFormDirty(form);
    this.props.setDirty(setDirty);
  };

  domainesContain = (domaine: string): boolean =>
    domaine && this.state.domaines.some((domain) => domain.label === domaine);

  langsContain = (lang: string): boolean =>
    lang && this.state.langs.some((langu) => langu.label === lang);

  selectAutoSource = (type: 'lang' | 'domaine', label: string, fromId?: boolean): AutocompleteSource => {
    if (!label) return null;
    let array: AutocompleteSource[];
    let selectedArray: AutocompleteSource[];
    if (type === 'lang') {
      array = this.state.langs;
      selectedArray = this.state.selectedLangs;
    } else {
      array = this.state.domaines;
      selectedArray = this.state.selectedDomaines;
    }
    if (fromId && label.length === 2) {
      return array.find(((lang) =>
        lang.id.toLowerCase() === label.toLowerCase()))
        || selectedArray.find((lang) =>
          lang.id.toLowerCase() === label.toLowerCase());
    }
    return array.find(((lang) =>
      lang.label.toLowerCase() === label.toLowerCase()))
      || selectedArray.find((lang) =>
        lang.label.toLowerCase() === label.toLowerCase());
  };

  getValueIfChanged = (formValue: string, key: string): string => {
    if (formValue === this.state.initialValues[key]
        || (!formValue && !this.state.initialValues[key])) {
      return null;
    }
    return formValue || '';
  };

  setLoading = (loading: boolean): void => {
    this.setState(() => ({ isLoading: loading }));
  };

  saveValues = async (values: FormValues): Promise<{ [FORM_ERROR]: FieldErrorDTO[] }> => {
    this.setLoading(true);
    try {
      const newDomaines = values.domaines || [];
      const newLangs = values.langs || [];
      const langInField = this.selectAutoSource('lang', values.langsSelect);
      if (langInField) {
        newLangs.push(langInField.id);
      }
      const domaineInField = this.selectAutoSource('domaine', values.domainesSelect);
      if (domaineInField) {
        newDomaines.push(domaineInField.id);
      }
      const valeus: CompteProfilProModifyDTO = {
        email: this.getValueIfChanged(values.email, 'email'),
        idnRattachement: values.idnRattachement,
        telephoneFixe: this.getValueIfChanged(values.telephoneFixe, 'telephoneFixe'),
        telephoneMobile: this.getValueIfChanged(values.telephoneMobile, 'telephoneMobile'),
        statut: values.statut,
        mandat: values.fonction,
        domaineDactivite: newDomaines,
      };
      let retErrors: FieldErrorDTO[] = [];
      const promises: Promise<{ [FORM_ERROR]: FieldErrorDTO[] }>[] = [];
      promises.push(compteApi.modifyProfilPro(valeus)
        .then(() => compteApi.getProfilePro(this.props.idnRattachement)
          .then((profileDetails) => this.setState({ profileDetails, submitted: true })))
        .then(() => this.setState({ domainesDirty: false }))
        .catch((err) => {
          if (typeof err.then === 'function') {
            return err.then((e: ErrorDTO) => ({ [FORM_ERROR]: e.fieldErrors }));
          }
          notifyErrorWithMessage(I18n.t('message.ERROR_MODIF'));
          return null;
        }));
      promises.push(compteApi.modifyMyIdentity({
        ...this.state.myIdentity,
        languages: newLangs,
      })
        .then(() => compteApi.fetchMyIdentity()
          .then((myIdentity) => {
            this.setState({ myIdentity });
          }))
        .then(() => this.setState({ langsDirty: false }))
        .catch((err) => {
          if (typeof err === 'function') {
            return err.then((e: ErrorDTO) => ({ [FORM_ERROR]: e.fieldErrors }));
          }
          notifyErrorWithMessage(I18n.t('message.ERROR_MODIF'));
          return null;
        }));
      return Promise.all(promises)
        .then((e) => {
          e.forEach((err) => {
            if (err && err[FORM_ERROR]) {
              retErrors = retErrors.concat(err[FORM_ERROR]);
            }
          });
          if (!retErrors.length) {
            this.props.setDirty(false);
            notifySuccess(I18n.t('message.SUCCESS'));
            this.props.onClose();
          }
          return { [FORM_ERROR]: retErrors };
        });
      // const json = await
    } catch (err) {
      this.setLoading(false);
      return { [FORM_ERROR]: [] };
    }
  };

  handleEdit = async (values: FormValues): Promise<{ [FORM_ERROR]: FieldErrorDTO[] }> => {
    if (this.state.confirmModalOpen || (
      this.state.profileDetails.typeEntite !== TypeEntityEnum.OFFICE
      && this.state.profileDetails.typeEntite !== TypeEntityEnum.INSTANCE
    )) {
      return this.saveValues(values)
        .finally(() => this.setState(() => ({ confirmModalOpen: false, isLoading: false })));
    }
    this.setState(() => ({ confirmModalOpen: true }));
    return null;
  };

  getErrorMessage = (errors: FieldErrorDTO[], fieldName: string): string => {
    if (errors) {
      return errors.find((error: FieldErrorDTO) => error.field === fieldName)?.message;
    }
    return '';
  };

  render(): ReactNode {
    if (!this.state.profileDetails) {
      return false;
    }
    const initialValues = this.state.initialValues;
    return (
      <Form
        onSubmit={this.handleEdit}
        initialValues={initialValues}
        render={({
          handleSubmit,
          form,
          submitError,
        }) => {
          const resetAutocompleteField = (field: 'lang' | 'domaine') => {
            form.change(`${field}sSelect` as keyof FormValues, '');
            this.onFormChange(form);
          };
          return (
            <>
              <ConfirmationModal
                isOpen={this.state.confirmModalOpen}
                onClose={() => this.openModal('save')}
                titleI18nKey="PROFESSIONAL_INFORMATION.CONFIRM_SUBMIT_TITLE"
                subtTitleI18nKey="PROFESSIONAL_INFORMATION.CONFIRM_SUBMIT_SUBTITLE"
                onConfirm={form.submit}
                onCancel={() => this.openModal('save')}
                confirmeDisabled={this.state.isLoading}
                isLoading={this.state.isLoading}
              />
              <ConfirmationModal
                isOpen={this.state.langModalOpen}
                onClose={() => this.openModal('lang')}
                titleI18nKey=""
                subtTitleI18nKey={`${I18n.t('PROFESSIONAL_INFORMATION.CONFIRM_LANG1')} ${form.getFieldState('langsSelect')?.value} ${I18n.t('PROFESSIONAL_INFORMATION.CONFIRM_LANG2')}`}
                onConfirm={() => {
                  form.submit();
                  resetAutocompleteField('lang');
                  this.openModal('lang');
                }}
                onCancel={() => {
                  resetAutocompleteField('lang');
                  form.submit();
                  this.openModal('lang');
                }}
                confirmeDisabled={false}
              />
              <ConfirmationModal
                isOpen={this.state.domaineModalOpen}
                onClose={() => this.openModal('domaine')}
                titleI18nKey=""
                subtTitleI18nKey={`${I18n.t('PROFESSIONAL_INFORMATION.CONFIRM_LANG1')} ${form.getFieldState('domainesSelect')?.value} ${I18n.t('PROFESSIONAL_INFORMATION.CONFIRM_DOMAIN2')}`}
                onConfirm={() => {
                  form.submit();
                  resetAutocompleteField('domaine');
                  this.openModal('domaine');
                }}
                onCancel={() => {
                  resetAutocompleteField('domaine');
                  form.submit();
                  this.openModal('domaine');
                }}
                confirmeDisabled={false}
              />
              <form
                className="professional-information-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const dirty = this.isFormDirty(form);
                  if (dirty) {
                    if (this.langsContain(form.getFieldState('langsSelect').value)) {
                      this.openModal('lang');
                    } else if (this.domainesContain(form.getFieldState('domainesSelect').value)) {
                      this.openModal('domaine');
                    } else if (form.getRegisteredFields().filter((fieldName) => fieldName !== 'langsSelect')
                      .some((fieldName) => (
                        (form.getFieldState(fieldName as keyof FormValues).dirtySinceLastSubmit
                            && this.state.submitted)
                          || (form.getFieldState(fieldName as keyof FormValues).dirty
                            && !this.state.submitted)
                      ))
                      || this.state.langsDirty || this.state.domainesDirty) {
                      handleSubmit(e);
                      resetAutocompleteField('lang');
                      resetAutocompleteField('domaine');
                    }
                  } else {
                    this.props.onClose();
                  }
                }}
                onChange={() => this.onFormChange(form)}
              >
                <Field
                  name="fonction"
                  component={TextInput}
                  placeholder={I18n.t('COLLABORATORS_INFORMATION.FONCTION')}
                  type="text"
                  readOnly
                />
                {this.state.domaines.length && (
                <ChipsListAutocomplete
                  type="domaine"
                  setDirty={() => this.setState({ domainesDirty: true })}
                  resetField={() => resetAutocompleteField('domaine')}
                  className="domaines-autocomplete"
                  subtitleI18n="COLLABORATORS_INFORMATION.DOMAIN"
                  placeholderI18n="SEARCH.DOMAINES"
                  allElements={this.state.domaines}
                  selectedElements={this.state.profileDetails?.domaineDactivite}
                  customError={this.state.domaineError}
                  onFormChange={() => this.onFormChange(form)}
                  updateField={(domaines) => form.change('domaines', domaines)}
                />
                )}
                <Field
                  name="email"
                  component={TextInput}
                  placeholder={I18n.t('COLLABORATORS_INFORMATION.MAIL_PRO')}
                  type="text"
                  validate={validateEmail}
                  customError={this.getErrorMessage(submitError, 'email')}
                />
                <Field
                  name="telephoneFixe"
                  component={TextInput}
                  placeholder={I18n.t('COLLABORATORS_INFORMATION.TEL_FIXE')}
                  type="text"
                  validate={validatePhone}
                  customError={this.getErrorMessage(submitError, 'telephoneFixe')}
                />
                <Field
                  name="telephoneMobile"
                  component={TextInput}
                  placeholder={I18n.t('COLLABORATORS_INFORMATION.TEL_MOBILE')}
                  type="text"
                  validate={validatePhone}
                  customError={this.getErrorMessage(submitError, 'telephoneMobile')}
                />
                <ChipsListAutocomplete
                  type="lang"
                  setDirty={() => this.setState({ langsDirty: true })}
                  resetField={() => resetAutocompleteField('lang')}
                  className="langs-autocomplete"
                  subtitleI18n="PROFESSIONAL_INFORMATION.SPOKEN_LANGUAGES"
                  placeholderI18n="SEARCH.LANGUAGES"
                  allElements={this.state.langs}
                  selectedElements={this.state.myIdentity?.languages}
                  customError={this.state.langError}
                  onFormChange={() => this.onFormChange(form)}
                  updateField={(langs) => form.change('langs', langs)}
                />
                <div className="button-container">
                  <Button
                    type="third"
                    onClick={this.props.onClose}
                    i18nKey="actions.CANCEL"
                  />
                  <Button
                    type="primary"
                    buttonType="submit"
                    i18nKey="actions.SAVE"
                    isLoading={this.state.isLoading}
                    disabled={this.state.isLoading}
                  />
                </div>
              </form>
            </>
          );
        }}
      />
    );
  }
}

export default ProfessionalInformationForm;
